import { useState, useEffect, useCallback } from 'react';
import { Upload, FileSpreadsheet, Save, PlayCircle, RefreshCw, AlertTriangle, X } from 'lucide-react';
import { paymentReconApi } from '../../services/paymentReconApi';
import { useToast } from '../ui/Toast';
import { useTableSort } from '../../hooks/useTableSort';
import { SortableTh } from './SortableTh';
import { formatDate } from '../../utils/paymentReconFormat';
import type { ReconBankAccount, ReconTemplate, ReconImport, ColumnMapping } from '../../types/paymentRecon';

interface Props {
    canEdit: boolean;
    companyCode: string;
    bankAccounts: ReconBankAccount[];
    templates: ReconTemplate[];
    onTemplatesChanged: () => void;
    onImported: () => void;
}

const EMPTY_MAPPING: ColumnMapping = {
    bookingDate: '', valueDate: '', amount: '', amountDebit: '', currency: '',
    counterparty: '', reference: '', decimalSeparator: ',', sheetIndex: null, sheetName: '', headerRow: 1,
    defaultCurrency: 'EUR', creditsOnly: true,
};

const clearColumns = (): Partial<ColumnMapping> => ({ bookingDate: '', valueDate: '', amount: '', amountDebit: '', counterparty: '', reference: '', currency: '' });

/**
 * A real header row has several DIFFERENT labels. A banner ("Votre situation financière
 * au 29/07/2026" merged across the sheet) or an empty row yields one repeated value — a
 * sure sign the wrong sheet or the wrong header row is selected, and the reason the column
 * pickers would otherwise offer nothing but that banner.
 */
function headerRowLooksWrong(headers: string[]): boolean {
    const distinct = new Set(headers.map(h => (h ?? '').trim()).filter(Boolean));
    return distinct.size < 2;
}

function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const dataUrl = reader.result as string;
            resolve(dataUrl.split(',')[1] || '');
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const FIELD_DEFS: Array<{ key: keyof ColumnMapping; label: string; required?: boolean }> = [
    { key: 'bookingDate', label: 'Booking date', required: true },
    { key: 'valueDate', label: 'Value date' },
    { key: 'amount', label: 'Amount (credit)', required: true },
    { key: 'amountDebit', label: 'Debit column (optional)' },
    { key: 'counterparty', label: 'Counterparty', required: true },
    { key: 'reference', label: 'Reference / description', required: true },
    { key: 'currency', label: 'Currency column' },
];

export function ImportTab({ canEdit, companyCode, bankAccounts, templates, onTemplatesChanged, onImported }: Props) {
    const { toast } = useToast();
    const [bankAccountId, setBankAccountId] = useState<number | ''>('');
    const [templateId, setTemplateId] = useState<number | ''>('');
    const [fileBase64, setFileBase64] = useState<string | null>(null);
    const [fileName, setFileName] = useState('');
    const [fileFormat, setFileFormat] = useState('csv');
    const [headers, setHeaders] = useState<string[]>([]);
    const [sheets, setSheets] = useState<string[]>([]);
    const [mapping, setMapping] = useState<ColumnMapping>(EMPTY_MAPPING);
    const [sample, setSample] = useState<Record<string, unknown>[]>([]);
    const [counts, setCounts] = useState<{ total: number; credit: number } | null>(null);
    const [busy, setBusy] = useState(false);
    const [templateName, setTemplateName] = useState('');
    const [imports, setImports] = useState<ReconImport[]>([]);
    const [dupWarning, setDupWarning] = useState<{ skipped: number; overlaps: number } | null>(null);
    // The chosen sheet + header row do not point at a real table (see headerRowLooksWrong).
    const [badHeaderRow, setBadHeaderRow] = useState(false);

    const loadImports = useCallback(async () => {
        try {
            const res = await paymentReconApi.listImports(companyCode);
            setImports(res.imports);
        } catch { /* ignore */ }
    }, [companyCode]);
    useEffect(() => { void loadImports(); }, [loadImports]);

    const { sorted: sortedImports, sort: importSort, toggleSort: toggleImportSort } = useTableSort(imports, {
        file: r => r.FileName,
        period: r => r.StatementFrom,
        credits: r => r.RowsCredit,
        completeness: r => r.Unresolved,
        by: r => r.CreatedBy,
    });

    const activeBankAccounts = bankAccounts.filter(b => b.IsActive);

    useEffect(() => {
        if (activeBankAccounts.length === 1 && !bankAccountId) setBankAccountId(activeBankAccounts[0].Id);
    }, [activeBankAccounts, bankAccountId]);

    const onSelectTemplate = (id: number | '') => {
        setTemplateId(id);
        const tpl = templates.find(t => t.Id === id);
        if (tpl) {
            setMapping({ ...EMPTY_MAPPING, ...tpl.mapping });
            setFileFormat(tpl.FileFormat || 'csv');
        }
    };

    // Pre-select the bank's default mapping template. Starting on "New / inline mapping"
    // meant every upload began with no sheet and no header row, so a multi-sheet workbook
    // fell back to sheet 1 — for CIC the account-summary tab, whose only "header" is a
    // merged banner. The column pickers then offered nothing but that banner text.
    useEffect(() => {
        if (templateId !== '' || templates.length === 0) return;
        const preferred = templates.find(t => t.IsDefault) ?? (templates.length === 1 ? templates[0] : null);
        if (preferred) onSelectTemplate(preferred.Id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templates, templateId]);

    // Re-detect headers for the currently chosen sheet + header row (no column mapping needed).
    const detectHeaders = useCallback(async (b64: string, fmt: string, over: Partial<ColumnMapping>) => {
        try {
            const res = await paymentReconApi.previewImport({
                companyCode, fileBase64: b64, fileFormat: fmt,
                mapping: { ...EMPTY_MAPPING, ...over, ...clearColumns(), creditsOnly: false },
            });
            setHeaders(res.headers);
            setSheets(res.sheets);
            setBadHeaderRow(headerRowLooksWrong(res.headers));
            return res;
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Could not read file', 'error');
            return null;
        }
    }, [toast, companyCode]);

    const onFile = async (file: File | null) => {
        if (!file) return;
        setFileName(file.name);
        const fmt = /\.xlsx?$/i.test(file.name) ? 'xlsx' : 'csv';
        setFileFormat(fmt);
        const b64 = await fileToBase64(file);
        setFileBase64(b64);
        // Detect sheets + headers (honours a sheet/header-row already set by a template).
        const res = await detectHeaders(b64, fmt, { sheetIndex: mapping.sheetIndex, sheetName: mapping.sheetName, headerRow: mapping.headerRow });
        if (res) {
            // A template that still pins the sheet by NAME breaks as soon as the bank renames
            // the tab on the next download — say so instead of silently reading sheet 1.
            if (res.sheets.length > 1 && res.sheetResolvedBy === 'default' && (mapping.sheetIndex || mapping.sheetName)) {
                toast(`The sheet saved in this template is not in this file — pick it again below (it is then remembered by position)`, 'error');
            } else if (res.sheets.length > 1 && !mapping.sheetIndex && !mapping.sheetName) {
                toast(`Workbook has ${res.sheets.length} sheets — pick the one with the transactions`, 'info');
            } else {
                toast(`Detected ${res.headers.length} columns`, 'info');
            }
            // Keep the stored name in step with the sheet actually read (display only).
            if (res.sheetIndex) setMapping(m => ({ ...m, sheetIndex: res.sheetIndex, sheetName: res.sheetName }));

            // A template whose columns are no longer in the file (the bank renamed its
            // headers — CIC switched to French: Message→Libellé) would otherwise import
            // silent NULLs. Say so here; the API refuses it as well.
            const gone = FIELD_DEFS
                .map(f => ({ label: f.label, col: mapping[f.key] as string | undefined }))
                .filter(x => x.col && res.headers.length > 0 && !res.headers.includes(x.col));
            if (gone.length > 0) {
                toast(`This file no longer has: ${gone.map(g => `"${g.col}"`).join(', ')} — the bank renamed its columns. Re-map them below and update the template.`, 'error');
            }
        }
    };

    // The sheet is pinned by POSITION. Bank exports (CIC) rename the transactions tab on
    // every download, so a name-based template silently fell back to worksheet 1.
    const onSheet = (index: number | null) => {
        const name = index ? (sheets[index - 1] ?? '') : '';
        setMapping(m => ({ ...m, sheetIndex: index, sheetName: name, ...clearColumns() }));
        if (fileBase64) void detectHeaders(fileBase64, fileFormat, { sheetIndex: index, sheetName: name, headerRow: mapping.headerRow });
    };

    const onHeaderRow = (rowNum: number) => {
        setMapping(m => ({ ...m, headerRow: rowNum, ...clearColumns() }));
        if (fileBase64) void detectHeaders(fileBase64, fileFormat, { sheetIndex: mapping.sheetIndex, sheetName: mapping.sheetName, headerRow: rowNum });
    };

    const preview = async () => {
        if (!fileBase64) { toast('Upload a file first', 'info'); return; }
        setBusy(true);
        try {
            const res = await paymentReconApi.previewImport({ companyCode, fileBase64, fileFormat, mapping });
            setHeaders(res.headers);
            setSample(res.sampleRows as Record<string, unknown>[]);
            setCounts({ total: res.totalDataRows, credit: res.creditRows });
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Preview failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const selectedTemplate = templates.find(t => t.Id === templateId) ?? null;

    /** Overwrite the selected template in place, or save the mapping under a new name. */
    const saveTemplate = async (asNew: boolean) => {
        const name = asNew ? templateName.trim() : (selectedTemplate?.Name ?? '');
        if (!name) { toast('Enter a template name', 'info'); return; }
        try {
            await paymentReconApi.saveTemplate({
                id: asNew ? undefined : selectedTemplate?.Id,
                name,
                bankAccountId: bankAccountId || null,
                fileFormat,
                mapping,
                isDefault: asNew ? templates.length === 0 : selectedTemplate?.IsDefault,
            });
            toast(asNew ? 'Template saved' : `Template "${name}" updated`, 'success');
            if (asNew) setTemplateName('');
            onTemplatesChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to save template', 'error');
        }
    };

    const doImport = async () => {
        if (!bankAccountId) { toast('Choose a bank account', 'info'); return; }
        if (!fileBase64) { toast('Upload a file first', 'info'); return; }
        // Point at the real problem first: if the header row is a banner, asking the user to
        // "map the required columns" only invites them to map that banner into every field.
        if (badHeaderRow) {
            toast('Fix the sheet and header row first — the selected row is a title banner, not the column titles.', 'error');
            return;
        }
        // Every field marked * is genuinely required. Counterparty/reference used to be
        // marked required but never enforced — importing without the payment text produced
        // rows that look new on every re-upload (38 duplicates on 2026-07-28).
        const missing = FIELD_DEFS
            .filter(f => f.required && !(mapping[f.key] as string | undefined))
            .map(f => f.label);
        if (missing.length > 0) {
            toast(`Map these columns first: ${missing.join(', ')}. If the bank has only one text column, map it to both Counterparty and Reference.`, 'error');
            return;
        }
        setBusy(true);
        try {
            const res = await paymentReconApi.createImport({
                companyCode,
                bankAccountId: Number(bankAccountId),
                templateId: templateId || null,
                mapping,
                fileBase64,
                fileName,
                fileFormat,
            });
            const skipped = res.skippedDuplicates ?? 0;
            toast(`Imported ${res.imported} credits · ${res.autoMatched} auto-matched · ${res.needsReview} to review${skipped > 0 ? ` · ${skipped} skipped` : ''}`, 'success');
            if (skipped > 0) {
                setDupWarning({ skipped, overlaps: res.overlappingImports });
            } else {
                setDupWarning(null);
            }
            setFileBase64(null);
            setFileName('');
            setSample([]);
            setCounts(null);
            void loadImports();
            onImported();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Import failed', 'error');
        } finally {
            setBusy(false);
        }
    };

    const colSelect = (key: keyof ColumnMapping, label: string, required?: boolean) => (
        <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-slate-600">{label}{required && <span className="text-rose-500"> *</span>}</span>
            <select
                value={(mapping[key] as string) ?? ''}
                onChange={(e) => setMapping(m => ({ ...m, [key]: e.target.value }))}
                className="rounded border border-slate-300 px-2 py-1.5 text-sm"
            >
                <option value="">—</option>
                {headers.map((h, idx) => <option key={`${idx}:${h}`} value={h}>{h || `(column ${idx + 1})`}</option>)}
            </select>
        </label>
    );

    if (!canEdit) {
        return <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">You have view-only access.</div>;
    }

    return (
        <div className="space-y-6">
            {dupWarning && (
                <div className="flex items-start justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span>
                            <strong>{dupWarning.skipped}</strong> row(s) were skipped because they already exist for this bank
                            account (same date, amount &amp; payer)
                            {dupWarning.overlaps > 0 ? `, across ${dupWarning.overlaps} overlapping import(s)` : ''}.
                            They were not imported again — no duplicates were created.
                        </span>
                    </div>
                    <button onClick={() => setDupWarning(null)} title="Dismiss" className="rounded p-0.5 text-amber-500 hover:bg-amber-100"><X className="h-4 w-4" /></button>
                </div>
            )}

            {/* Upload + config */}
            <div className="rounded-lg border border-slate-200 p-4">
                <div className="grid gap-4 md:grid-cols-2">
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-slate-600">Bank account *</span>
                        <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value ? Number(e.target.value) : '')} className="rounded border border-slate-300 px-2 py-1.5 text-sm">
                            <option value="">Choose…</option>
                            {activeBankAccounts.map(b => <option key={b.Id} value={b.Id}>{b.Name} ({b.Currency})</option>)}
                        </select>
                        {activeBankAccounts.length === 0 && <span className="text-xs text-amber-600">No active bank accounts — add one in Settings.</span>}
                    </label>
                    <label className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-slate-600">Mapping template</span>
                        <select value={templateId} onChange={(e) => onSelectTemplate(e.target.value ? Number(e.target.value) : '')} className="rounded border border-slate-300 px-2 py-1.5 text-sm">
                            <option value="">New / inline mapping</option>
                            {templates.map(t => <option key={t.Id} value={t.Id}>{t.Name}</option>)}
                        </select>
                    </label>
                </div>

                <div className="mt-4">
                    <label className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 py-6 text-sm text-slate-500 hover:border-amber-300 hover:bg-amber-50">
                        <Upload className="h-5 w-5" />
                        {fileName || 'Drop or choose a CSV / Excel bank export'}
                        <input type="file" accept=".csv,.xlsx,.xls,text/csv" className="hidden" onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
                    </label>
                </div>

                {fileFormat === 'xlsx' && sheets.length > 1 && (
                    <div className="mt-4">
                        <label className="flex flex-col gap-1 md:w-2/3">
                            <span className="text-xs font-medium text-slate-600">Sheet / tab<span className="text-rose-500"> *</span></span>
                            <select
                                value={mapping.sheetIndex ?? ''}
                                onChange={(e) => onSheet(e.target.value ? Number(e.target.value) : null)}
                                className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                            >
                                <option value="">Choose the sheet with the transactions…</option>
                                {sheets.map((s, i) => <option key={`${i}:${s}`} value={i + 1}>{i + 1}. {s}</option>)}
                            </select>
                            <span className="text-xs text-slate-400">
                                This workbook has {sheets.length} sheets — pick the one holding the line items, then set the header row.
                                The template remembers the sheet <strong>by position</strong>, so a bank that renames the tab on every
                                download keeps working.
                            </span>
                        </label>
                    </div>
                )}

                {badHeaderRow && headers.length > 0 && (
                    <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                        <span>
                            Row {mapping.headerRow ?? 1} of {sheets.length > 1 ? `sheet ${mapping.sheetIndex ?? 1} “${sheets[(mapping.sheetIndex ?? 1) - 1] ?? ''}”` : 'this file'} is
                            not a header row — every cell reads “{headers.find(Boolean) ?? '(empty)'}”. That is a title banner, not the
                            column titles. {sheets.length > 1 && <>Pick the sheet holding the transactions (for CIC that is sheet 2, not the account overview) and </>}
                            set the header row to the line with Date / Débit / Crédit. Don’t map the columns to this text.
                        </span>
                    </div>
                )}

                {headers.length > 0 && (
                    <div className="mt-4">
                        <h4 className="mb-2 text-sm font-semibold text-slate-700">Column mapping</h4>
                        <div className="grid gap-3 md:grid-cols-3">
                            {FIELD_DEFS.map(f => colSelect(f.key, f.label, f.required))}
                            <label className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-slate-600">Decimal separator</span>
                                <select value={mapping.decimalSeparator} onChange={(e) => setMapping(m => ({ ...m, decimalSeparator: e.target.value as ',' | '.' }))} className="rounded border border-slate-300 px-2 py-1.5 text-sm">
                                    <option value=",">comma (1.234,56)</option>
                                    <option value=".">dot (1,234.56)</option>
                                </select>
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-slate-600">Header row</span>
                                <input type="number" min={1} value={mapping.headerRow ?? 1} onChange={(e) => onHeaderRow(Number(e.target.value))} className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                            </label>
                            <label className="flex flex-col gap-1">
                                <span className="text-xs font-medium text-slate-600">Data starts at row</span>
                                <input
                                    type="number" min={1}
                                    placeholder={`${(mapping.headerRow ?? 1) + 1}`}
                                    value={mapping.dataStartRow ?? ''}
                                    onChange={(e) => setMapping(m => ({ ...m, dataStartRow: e.target.value ? Number(e.target.value) : undefined }))}
                                    className="rounded border border-slate-300 px-2 py-1.5 text-sm"
                                />
                            </label>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            <button onClick={preview} disabled={busy} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                                <PlayCircle className="h-4 w-4" /> Preview rows
                            </button>
                            {selectedTemplate && (
                                <button onClick={() => void saveTemplate(false)} className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm text-amber-800 hover:bg-amber-100">
                                    <Save className="h-4 w-4" /> Update “{selectedTemplate.Name}”
                                </button>
                            )}
                            <input value={templateName} onChange={(e) => setTemplateName(e.target.value)} placeholder="Save mapping as…" className="rounded border border-slate-300 px-2 py-1.5 text-sm" />
                            <button onClick={() => void saveTemplate(true)} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50">
                                <Save className="h-4 w-4" /> Save as new
                            </button>
                            <button onClick={doImport} disabled={busy} className="ml-auto inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50">
                                <FileSpreadsheet className="h-4 w-4" /> Import
                            </button>
                        </div>

                        {counts && (
                            <div className="mt-2 text-xs text-slate-500">{counts.credit} incoming credits of {counts.total} rows will be imported.</div>
                        )}
                        {sample.length > 0 && (
                            <div className="mt-3 overflow-auto rounded border border-slate-100">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-slate-50 text-left text-slate-500">
                                        <tr><th className="px-2 py-1">Date</th><th className="px-2 py-1">Amount</th><th className="px-2 py-1">Counterparty</th><th className="px-2 py-1">Reference</th></tr>
                                    </thead>
                                    <tbody>
                                        {sample.map((r, i) => (
                                            <tr key={i} className="border-t border-slate-50">
                                                <td className="px-2 py-1">{String((r as { bookingDate?: string }).bookingDate ?? '')}</td>
                                                <td className="px-2 py-1">{String((r as { amount?: number }).amount ?? '')}</td>
                                                <td className="px-2 py-1">{String((r as { counterparty?: string }).counterparty ?? '')}</td>
                                                <td className="max-w-xs truncate px-2 py-1">{String((r as { reference?: string }).reference ?? '')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Import history */}
            <div>
                <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-700">Import history</h4>
                    <button onClick={() => void loadImports()} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
                </div>
                {imports.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 py-6 text-center text-sm text-slate-400">No imports yet.</div>
                ) : (
                    <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
                        <table className="min-w-full divide-y divide-slate-200 text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                                <tr>
                                    <SortableTh label="File" sortKey="file" sort={importSort} onSort={toggleImportSort} />
                                    <SortableTh label="Period" sortKey="period" sort={importSort} onSort={toggleImportSort} />
                                    <SortableTh label="Credits" sortKey="credits" sort={importSort} onSort={toggleImportSort} className="text-right" />
                                    <SortableTh label="Completeness" sortKey="completeness" sort={importSort} onSort={toggleImportSort} />
                                    <SortableTh label="By" sortKey="by" sort={importSort} onSort={toggleImportSort} />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {sortedImports.map(imp => (
                                    <tr key={imp.Id} className="hover:bg-slate-50">
                                        <td className="max-w-xs truncate px-3 py-2 text-slate-700" title={imp.FileName}>{imp.FileName}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDate(imp.StatementFrom)} – {formatDate(imp.StatementTo)}</td>
                                        <td className="px-3 py-2 text-right text-slate-700">{imp.RowsCredit}</td>
                                        <td className="px-3 py-2">
                                            {imp.Unresolved === 0
                                                ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-800">all resolved</span>
                                                : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">{imp.Unresolved} unresolved</span>}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-400">{imp.CreatedBy}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
